import { useApi } from '@backstage/frontend-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import type { Notification } from '@backstage/plugin-notifications-common';
import {
  Badge,
  Button,
  cn,
  isExternalLink,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@vivicta/ui';
import { Bell } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { absoluteTime, relativeTime, toDate } from '../notifications/utils';
import { useUnreadNotificationsCount } from './useUnreadNotificationsCount';

/** How many of the latest notifications the bell's preview shows. */
const PREVIEW_LIMIT = 5;

const PREVIEW_SKELETON_WIDTHS = [
  { title: 'w-3/4', description: 'w-11/12' },
  { title: 'w-2/3', description: 'w-3/4' },
  { title: 'w-1/2', description: 'w-5/6' },
];

type Preview =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ready'; notifications: Notification[] };

const PreviewItem = ({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}) => {
  const { payload, created, read } = notification;
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className="flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
      >
        <span
          aria-hidden="true"
          className={cn(
            'mt-1.5 size-2 shrink-0 rounded-full',
            read ? 'bg-transparent' : 'bg-primary',
          )}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className={cn(
              'break-words text-sm',
              read
                ? 'font-medium text-muted-foreground'
                : 'font-semibold text-foreground',
            )}
          >
            {payload.title}
            {!read && <span className="sr-only"> (unread)</span>}
          </span>
          {payload.description && (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {payload.description}
            </span>
          )}
          <time
            dateTime={toDate(created).toISOString()}
            title={absoluteTime(created)}
            className="text-xs tabular-nums text-muted-foreground"
          >
            {relativeTime(created)}
          </time>
        </span>
      </button>
    </li>
  );
};

const PreviewBody = ({
  preview,
  onOpen,
}: {
  preview: Preview;
  onOpen: (notification: Notification) => void;
}) => {
  if (preview.state === 'loading') {
    // Mirrors `PreviewItem`: unread dot, title, description and time lines.
    return (
      <div className="divide-y divide-border" aria-busy="true">
        {PREVIEW_SKELETON_WIDTHS.map(({ title, description }) => (
          <div key={title} aria-hidden="true" className="flex gap-3 px-4 py-3">
            <Skeleton className="mt-1.5 size-2 shrink-0 rounded-full" />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Skeleton className={cn('my-0.5 h-4', title)} />
              <Skeleton className={cn('my-0.5 h-3', description)} />
              <Skeleton className="my-0.5 h-3 w-14" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (preview.state === 'error') {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        Notifications couldn&apos;t be loaded. Open the full list to try again.
      </p>
    );
  }
  if (preview.notifications.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        No notifications yet.
      </p>
    );
  }
  return (
    <ul className="max-h-96 divide-y divide-border overflow-y-auto">
      {preview.notifications.map(notification => (
        <PreviewItem
          key={notification.id}
          notification={notification}
          onOpen={onOpen}
        />
      ))}
    </ul>
  );
};

/**
 * The top bar's bell: a preview of the latest notifications, with a link to
 * the full list. Opening one marks it read and follows its link.
 */
export const NotificationsMenu = () => {
  const notificationsApi = useApi(notificationsApiRef);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<Preview>({ state: 'loading' });
  const [countReload, setCountReload] = useState(0);
  const unreadCount = useUnreadNotificationsCount(countReload);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setPreview(current =>
      current.state === 'ready' ? current : { state: 'loading' },
    );
    notificationsApi
      .getNotifications({
        limit: PREVIEW_LIMIT,
        sort: 'created',
        sortOrder: 'desc',
      })
      .then(response => {
        if (!cancelled) {
          setPreview({ state: 'ready', notifications: response.notifications });
        }
      })
      .catch(() => {
        if (!cancelled) setPreview({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [notificationsApi, open]);

  const openNotification = (notification: Notification) => {
    setOpen(false);
    if (!notification.read) {
      notificationsApi
        .updateNotifications({ ids: [notification.id], read: true })
        .then(() => setCountReload(key => key + 1))
        .catch(() => undefined);
    }
    const link = notification.payload.link;
    if (!link) {
      navigate('/notifications');
    } else if (isExternalLink(link)) {
      globalThis.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate(link);
    }
  };

  const label =
    unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className="relative"
        >
          <Bell aria-hidden="true" className="size-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Notifications"
        className="w-[min(24rem,calc(100vw-1.5rem))] p-0"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <p className="text-sm font-semibold text-foreground">Notifications</p>
          {unreadCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {`${unreadCount} unread`}
            </span>
          )}
        </div>
        <PreviewBody preview={preview} onOpen={openNotification} />
        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
